import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, FileText, Activity, Network, BarChart3,
  Database, ArrowRight, Zap, MessageCircle, Menu, X, Check,
  ShieldAlert, ShieldCheck, Clock, TrendingUp, Building2, MapPin,
  Sparkles, Lock, ChevronRight,
} from "lucide-react";

const FEATURES = [
  { icon: Search, title: "Real-time Screening", desc: "Screen against 15+ Indian government databases including MHA, RBI, SEBI, ED, CBI", color: "#2563eb" },
  { icon: Activity, title: "Transaction Monitoring", desc: "Rule-based surveillance — catch structuring, cash threshold breaches, round-trip transactions automatically", color: "#ef4444" },
  { icon: FileText, title: "AI-Powered STR Generator", desc: "Generate FIU-IND compliant Suspicious Transaction Reports with AI narratives in 2 minutes", color: "#FF6B35" },
  { icon: Network, title: "Entity Intelligence", desc: "Visualize director relationships and shell companies. Identify hidden risk connections across the corporate registry.", color: "#10b981" },
  { icon: Database, title: "15+ India Data Sources", desc: "MHA UAPA, RBI Defaulters, SEBI Debarred, ED Cases, CBI Wanted, eCourts, MCA21, NCLT, GSTN", color: "#FFD700" },
  { icon: BarChart3, title: "Compliance Reports", desc: "Pre-built RBI/FIU-IND reports with branded PDF exports — ready for regulatory inspection", color: "#8B0000" },
];

const SOURCES = [
  "MHA UAPA Terrorist List", "RBI Wilful Defaulters", "SEBI Debarred Entities",
  "ED Money Laundering Cases", "CBI Wanted List", "eCourts Criminal Records",
  "MCA21 Director Database", "NCLT Insolvency Cases", "GSTN Business Verification",
  "SFIO Corporate Fraud", "FATF Black & Grey Lists", "Global PEP Database",
  "Global Sanctions Network", "OpenSanctions",
];

const STATS = [
  { icon: Database,    value: 15,  suffix: "+", label: "India Govt Sources", accent: "#4f46e5" },
  { icon: ShieldCheck, value: 200, suffix: "+", label: "Watchlists Indexed", accent: "#22c55e" },
  { icon: Clock,       raw: "<60s",             label: "Real-time Screening", accent: "#f59e0b" },
  { icon: FileText,    raw: "RBI KYC MD 2016",  label: "Regulatory Framework", accent: "#38bdf8" },
];

// Trust badges: only verifiable claims (no false compliance certificates)
const TRUST_BADGES = [
  { icon: MapPin,      label: "Data hosted in India" },
  { icon: Lock,        label: "AES-256 encryption at rest" },
  { icon: ShieldCheck, label: "DPDP Act 2023 compliant" },
];

const PRICING = [
  {
    name: "Starter",
    price: "Custom",
    period: "",
    desc: "For early-stage NBFCs & fintechs",
    features: [
      "Up to 500 customer screenings",
      "15+ India watchlists",
      "Basic transaction monitoring",
      "Email support",
      "Single user",
    ],
    cta: "Start Free Trial",
    accent: "#2563eb",
  },
  {
    name: "Growth",
    price: "Custom",
    period: "",
    desc: "Most popular for growing NBFCs",
    features: [
      "Up to 5,000 customer screenings",
      "Bulk CSV screening",
      "STR auto-generation",
      "API access (1,000 calls/day)",
      "5 user seats + 2FA",
      "Priority email + WhatsApp support",
    ],
    cta: "Start Free Trial",
    accent: "#FFD700",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    desc: "For banks & large NBFCs",
    features: [
      "Unlimited screenings",
      "Custom rule engine",
      "Dedicated infra + SLA",
      "Unlimited users + SSO",
      "Audit-ready compliance reports",
      "Dedicated success manager",
    ],
    cta: "Talk to Sales",
    accent: "#10b981",
  },
];

// Update these from env or replace with actual numbers before launch.
const WHATSAPP_NUMBER = "919999999999"; // TODO: replace with sales WhatsApp
const DEMO_CALENDLY_URL = "mailto:shyam@rudrik.io?subject=Rudrik%20Demo%20Request"; // swap to Calendly when ready

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileNav(false);
  };

  return (
    <div style={{ background: "#080c12", minHeight: "100vh", color: "#f1f5f9", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Navbar */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 50, padding: "14px 40px",
        background: "rgba(8,12,18,0.92)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid #1e2530", display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div className="flex items-center gap-3">
          <img src="/assets/rudrik-logo-full.png" alt="Rudrik" style={{ height: "38px", width: "auto", objectFit: "contain" }} />
        </div>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-6" data-testid="landing-nav-links">
          <button onClick={() => scrollTo("features")} data-testid="nav-features" style={navLinkStyle}>Features</button>
          <button onClick={() => scrollTo("pricing")} data-testid="nav-pricing" style={navLinkStyle}>Pricing</button>
          <button onClick={() => scrollTo("data-sources")} data-testid="nav-about" style={navLinkStyle}>About</button>
          <a href={DEMO_CALENDLY_URL} data-testid="nav-book-demo" style={{ ...navLinkStyle, color: "#FFD700" }}>Book Demo</a>
          <button onClick={() => navigate("/login")} data-testid="landing-login-btn" style={{
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "8px",
            padding: "9px 22px", cursor: "pointer", color: "#fff", fontSize: "13px", fontWeight: 600,
          }}>
            Login
          </button>
        </div>

        {/* Mobile hamburger */}
        <button className="md:hidden" onClick={() => setMobileNav(v => !v)} data-testid="landing-nav-toggle"
          style={{ background: "transparent", border: "1px solid #1e2530", borderRadius: 8, padding: 8, cursor: "pointer", color: "#f1f5f9" }}>
          {mobileNav ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      {/* Mobile dropdown */}
      {mobileNav && (
        <div className="md:hidden" data-testid="landing-mobile-nav" style={{
          position: "sticky", top: 64, zIndex: 49, padding: "12px 24px", background: "#0d1117",
          borderBottom: "1px solid #1e2530", display: "flex", flexDirection: "column", gap: 10,
        }}>
          <button onClick={() => scrollTo("features")} style={navLinkStyle}>Features</button>
          <button onClick={() => scrollTo("pricing")} style={navLinkStyle}>Pricing</button>
          <button onClick={() => scrollTo("data-sources")} style={navLinkStyle}>About</button>
          <a href={DEMO_CALENDLY_URL} style={navLinkStyle}>Book Demo</a>
          <button onClick={() => navigate("/login")} style={{
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: 8,
            padding: "10px 18px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>Login</button>
        </div>
      )}

      {/* Hero + Stats — enterprise redesign (Feb-2026) */}
      <style>{`
        @keyframes rd-fade-up { from { opacity:0; transform: translateY(14px);} to { opacity:1; transform: none;} }
        @keyframes rd-pulse-dot { 0%,100% { transform: scale(1); opacity:1;} 50% { transform: scale(1.35); opacity:.55;} }
        @keyframes rd-shimmer { 0% { background-position:-140% 0;} 100% { background-position: 240% 0;} }
        .rd-fade-in > * { opacity: 0; animation: rd-fade-up .7s cubic-bezier(.2,.7,.3,1) forwards; }
        .rd-fade-in > *:nth-child(1){ animation-delay: .05s;}
        .rd-fade-in > *:nth-child(2){ animation-delay: .12s;}
        .rd-fade-in > *:nth-child(3){ animation-delay: .20s;}
        .rd-fade-in > *:nth-child(4){ animation-delay: .28s;}
        .rd-fade-in > *:nth-child(5){ animation-delay: .36s;}
        .rd-mock-card { animation: rd-fade-up .9s cubic-bezier(.2,.7,.3,1) .15s both; }
        .rd-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 12px 40px rgba(79,70,229,0.45);}
        .rd-btn-secondary:hover { border-color:#4f46e5; color:#f1f5f9;}
        .rd-stat-item:hover .rd-stat-icon { transform: scale(1.08); }
        .rd-stat-item { transition: background .3s ease; }
        .rd-stat-item:hover { background: rgba(79,70,229,0.04);}
        .rd-logo-pill:hover { color:#cbd5e1; border-color:#334155;}
        .rd-topo {
          background-image:
            radial-gradient(circle at 20% 30%, rgba(79,70,229,0.16) 0%, transparent 55%),
            radial-gradient(circle at 78% 12%, rgba(56,189,248,0.08) 0%, transparent 45%),
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 100% 100%, 100% 100%, 42px 42px, 42px 42px;
          background-position: 0 0, 0 0, -1px -1px, -1px -1px;
        }
        .rd-live-dot { animation: rd-pulse-dot 1.6s ease-in-out infinite; }
        .rd-progress-fill {
          background: linear-gradient(90deg,#f59e0b,#ef4444);
          background-size: 200% 100%;
          animation: rd-shimmer 2.4s linear infinite;
        }
        @media (min-width: 900px) {
          .rd-hero-grid { grid-template-columns: minmax(0,1.05fr) minmax(0,1fr) !important; gap: 60px !important;}
        }
      `}</style>

      <section className="rd-topo" style={{
        position: "relative", padding: "72px 32px 40px",
        borderBottom: "1px solid #14161f",
      }}>
        <div style={{
          maxWidth: 1240, margin: "0 auto",
          display: "grid", gridTemplateColumns: "1fr", gap: 40,
        }} className="rd-hero-grid">

          {/* LEFT — copy + CTAs + trust */}
          <div className="rd-fade-in" data-testid="hero-left">
            {/* Eyebrow */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "6px 14px", borderRadius: 999,
              background: "linear-gradient(135deg, rgba(79,70,229,0.14), rgba(56,189,248,0.08))",
              border: "1px solid rgba(79,70,229,0.28)",
              marginBottom: 22,
            }}>
              <Sparkles size={12} color="#a5b4fc" />
              <span style={{ fontSize: 11, fontWeight: 600, color: "#c7d2fe",
                              letterSpacing: 0.3 }}>
                India&apos;s First AML Compliance Intelligence Platform
              </span>
            </div>

            {/* Headline */}
            <h1 style={{
              fontSize: "clamp(38px, 5.2vw, 64px)", fontWeight: 700,
              lineHeight: 1.02, letterSpacing: "-1.6px", margin: 0,
              marginBottom: 18, color: "#f8fafc",
              fontFamily: "'Geist', 'Inter', 'DM Sans', ui-sans-serif, system-ui",
            }} data-testid="hero-headline">
              Screen against{" "}
              <span style={{
                background: "linear-gradient(120deg, #a5b4fc 0%, #4f46e5 55%, #38bdf8 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>15+ Indian Government</span>{" "}
              databases in real-time.
            </h1>

            {/* Sub */}
            <p style={{
              fontSize: 17, color: "#94a3b8", lineHeight: 1.55,
              maxWidth: 560, marginTop: 0, marginBottom: 28,
            }} data-testid="hero-subhead">
              Built for RBI KYC Master Directions 2016. Catch wilful defaulters,
              terrorists, PEPs, and fraud before they enter your system.
            </p>

            {/* CTAs */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12,
                            alignItems: "center", marginBottom: 34 }}>
              <button className="rd-btn-primary" data-testid="hero-cta-primary"
                onClick={() => navigate("/login")}
                style={{
                  background: "linear-gradient(135deg,#4f46e5 0%, #4338ca 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 12, padding: "14px 28px", cursor: "pointer",
                  color: "#fff", fontSize: 15, fontWeight: 600,
                  display: "inline-flex", alignItems: "center", gap: 8,
                  boxShadow: "0 8px 28px rgba(79,70,229,0.42)",
                  transition: "all .2s ease",
                }}>
                Start Free Screening <ArrowRight size={16} />
              </button>
              <a className="rd-btn-secondary" href={DEMO_CALENDLY_URL}
                 data-testid="hero-cta-secondary"
                style={{
                  background: "transparent", border: "1px solid #232735",
                  borderRadius: 12, padding: "13px 26px", color: "#cbd5e1",
                  fontSize: 15, fontWeight: 600, textDecoration: "none",
                  display: "inline-flex", alignItems: "center", gap: 8,
                  transition: "all .2s ease",
                }}>
                Book 30-min Demo <ChevronRight size={15} />
              </a>
            </div>

            {/* Trust strip — verifiable claims only */}
            <div data-testid="hero-trust-strip"
                 style={{ display: "flex", flexWrap: "wrap", gap: 18,
                            alignItems: "center" }}>
              {TRUST_BADGES.map((b) => {
                const I = b.icon;
                return (
                  <div key={b.label}
                       data-testid={`trust-badge-${b.label.replace(/\s+/g, "-").toLowerCase()}`}
                       style={{
                         display: "inline-flex", alignItems: "center", gap: 6,
                         color: "#94a3b8", fontSize: 12, fontWeight: 600,
                       }}>
                    <I size={14} color="#22c55e" /> {b.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT — Product mock: Screening Result card */}
          <div className="rd-mock-card" data-testid="hero-product-mock"
               style={{ position: "relative" }}>
            {/* Ambient glow */}
            <div aria-hidden style={{
              position: "absolute", inset: -30, zIndex: 0,
              background: "radial-gradient(circle at 55% 40%, rgba(79,70,229,0.28), transparent 65%)",
              filter: "blur(30px)",
            }} />
            <div style={{
              position: "relative", zIndex: 1,
              background: "linear-gradient(180deg,#0d1017 0%, #0a0d14 100%)",
              border: "1px solid #1e2333", borderRadius: 16,
              boxShadow: "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(79,70,229,0.06)",
              overflow: "hidden",
            }}>
              {/* Card header */}
              <div style={{
                padding: "14px 18px", borderBottom: "1px solid #1a1e2c",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "linear-gradient(180deg,#0f1320,#0b0f18)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="rd-live-dot" style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#22c55e", boxShadow: "0 0 12px #22c55e",
                    display: "inline-block",
                  }} />
                  <span style={{ color: "#22c55e", fontSize: 10,
                                  fontWeight: 800, letterSpacing: 1 }}>LIVE</span>
                  <span style={{ color: "#334155", fontSize: 12 }}>·</span>
                  <span style={{ color: "#94a3b8", fontSize: 12,
                                  fontWeight: 600 }}>Screening RDK-2461-A7F2</span>
                </div>
                <span style={{ color: "#475569", fontSize: 10,
                                fontFamily: "ui-monospace, monospace" }}>0.42s</span>
              </div>

              {/* Subject */}
              <div style={{ padding: "16px 18px",
                             borderBottom: "1px solid #14172255" }}>
                <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700,
                               letterSpacing: 1, textTransform: "uppercase",
                               marginBottom: 6 }}>Subject</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                    color: "#fff", fontWeight: 800, fontSize: 15,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>VM</div>
                  <div>
                    <div style={{ color: "#f1f5f9", fontSize: 15,
                                    fontWeight: 700 }}>Vijay Mallya</div>
                    <div style={{ color: "#94a3b8", fontSize: 11,
                                    fontFamily: "ui-monospace, monospace" }}>
                      Individual · PAN AAAPM1234A · IN
                    </div>
                  </div>
                </div>
              </div>

              {/* Alert banner */}
              <div style={{
                margin: "14px 14px 12px",
                padding: "14px 16px", borderRadius: 12,
                background: "linear-gradient(135deg, rgba(239,68,68,0.14), rgba(239,68,68,0.05))",
                border: "1px solid rgba(239,68,68,0.35)",
              }}>
                <div style={{ display: "flex", alignItems: "center",
                                justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ShieldAlert size={16} color="#fca5a5" />
                    <span style={{ color: "#fecaca", fontSize: 12,
                                    fontWeight: 800, letterSpacing: 0.4 }}>
                      2 MATCHES FOUND
                    </span>
                  </div>
                  <div style={{
                    padding: "2px 10px", borderRadius: 999,
                    background: "rgba(239,68,68,0.18)",
                    border: "1px solid rgba(239,68,68,0.45)",
                    color: "#fca5a5", fontSize: 10, fontWeight: 800,
                    letterSpacing: 0.7,
                  }}>RISK · 60 / 100 · MEDIUM</div>
                </div>
                {/* Progress */}
                <div style={{ background: "#0a0d14", borderRadius: 999,
                                height: 5, overflow: "hidden" }}>
                  <div className="rd-progress-fill"
                       style={{ width: "60%", height: "100%",
                                  borderRadius: 999 }} />
                </div>
              </div>

              {/* Match cards */}
              <div style={{ padding: "0 14px 12px",
                             display: "grid", gap: 6 }}>
                {[
                  { source: "RBI Wilful Defaulters", date: "12 Feb 2026", pts: 30, tag: "credit_defaulter" },
                  { source: "ED Enforcement Case",   date: "08 Feb 2026", pts: 30, tag: "money_laundering" },
                ].map((m) => (
                  <div key={m.source} data-testid={`mock-match-${m.tag}`} style={{
                    padding: "10px 12px",
                    background: "#0a0d14",
                    border: "1px solid #1a1e2c", borderRadius: 10,
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "#f1f5f9", fontSize: 12,
                                      fontWeight: 700 }}>{m.source}</div>
                      <div style={{ color: "#64748b", fontSize: 10,
                                      marginTop: 1 }}>
                        Updated {m.date} · <code style={{ color: "#a78bfa" }}>{m.tag}</code>
                      </div>
                    </div>
                    <div style={{ color: "#ef4444", fontSize: 15,
                                    fontWeight: 800,
                                    fontFamily: "ui-monospace, monospace" }}>
                      +{m.pts}
                    </div>
                  </div>
                ))}
              </div>

              {/* Card footer */}
              <div style={{
                padding: "10px 18px", borderTop: "1px solid #14172255",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "rgba(10,13,20,0.4)",
              }}>
                <div style={{ color: "#64748b", fontSize: 10,
                                display: "flex", alignItems: "center", gap: 6 }}>
                  <Database size={11} /> 15 India sources checked
                </div>
                <div style={{ color: "#22c55e", fontSize: 10, fontWeight: 700,
                                letterSpacing: 0.6 }}>
                  ✓ REPORT READY
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar — icons + count-up + dividers */}
      <section aria-label="Rudrik key numbers"
        style={{ padding: "24px 32px 44px",
                  borderBottom: "1px solid #14161f" }}>
        <div style={{
          maxWidth: 1240, margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 0,
          background: "linear-gradient(180deg,#0b0e17,#0a0d14)",
          border: "1px solid #14182a", borderRadius: 16,
          overflow: "hidden",
        }}>
          {STATS.map((s, i) => (
            <StatCell key={s.label} stat={s} showDivider={i > 0} />
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" style={{ padding: "60px 40px", maxWidth: "1200px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "28px", fontWeight: 700, textAlign: "center", marginBottom: "12px" }}>
          Everything You Need for <span style={{ color: "#2563eb" }}>Indian AML Compliance</span>
        </h2>
        <p style={{ fontSize: "14px", color: "#475569", textAlign: "center", marginBottom: "40px" }}>
          Built specifically for Indian fintechs, NBFCs, and banks
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
          {FEATURES.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.title} style={{
                background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px",
                padding: "24px", transition: "border-color 0.2s",
              }}>
                <div style={{
                  width: "40px", height: "40px", borderRadius: "10px",
                  background: `${f.color}12`, display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: "14px",
                }}>
                  <Icon style={{ width: "20px", height: "20px", color: f.color }} />
                </div>
                <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#f1f5f9", marginBottom: "8px" }}>{f.title}</h3>
                <p style={{ fontSize: "13px", color: "#94a3b8", lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ padding: "60px 40px", maxWidth: "1200px", margin: "0 auto" }}>
        <h2 style={{ fontSize: "28px", fontWeight: 700, textAlign: "center", marginBottom: "12px" }}>
          Simple, <span style={{ color: "#FFD700" }}>Transparent Pricing</span>
        </h2>
        <p style={{ fontSize: "14px", color: "#475569", textAlign: "center", marginBottom: "40px" }}>
          Designed for Indian NBFCs, banks, and fintechs. Get a tailored quote for your scale.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px", maxWidth: 1100, margin: "0 auto" }}>
          {PRICING.map(plan => (
            <div key={plan.name} data-testid={`pricing-${plan.name.toLowerCase()}`} style={{
              background: plan.highlight ? "rgba(255,215,0,0.04)" : "#0d1117",
              border: `1px solid ${plan.highlight ? "rgba(255,215,0,0.35)" : "#1e2530"}`,
              borderRadius: "14px", padding: "28px",
              position: "relative",
              boxShadow: plan.highlight ? "0 12px 40px rgba(255,215,0,0.08)" : "none",
            }}>
              {plan.highlight && (
                <div style={{
                  position: "absolute", top: -10, right: 20,
                  background: "#FFD700", color: "#080c12", fontSize: 10, fontWeight: 700,
                  padding: "3px 10px", borderRadius: 99, letterSpacing: 1, textTransform: "uppercase",
                }}>Most Popular</div>
              )}
              <h3 style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>{plan.name}</h3>
              <p style={{ fontSize: 12, color: "#475569", marginBottom: 16 }}>{plan.desc}</p>
              <div style={{ fontSize: 30, fontWeight: 700, color: plan.accent, lineHeight: 1.1 }}>
                {plan.price}<span style={{ fontSize: 14, color: "#475569", fontWeight: 500 }}>{plan.period}</span>
              </div>
              <div style={{ height: 1, background: "#1e2530", margin: "20px 0" }} />
              <ul style={{ listStyle: "none", padding: 0, margin: 0, marginBottom: 24 }}>
                {plan.features.map(feat => (
                  <li key={feat} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#cbd5e1", marginBottom: 10 }}>
                    <Check style={{ width: 14, height: 14, color: plan.accent, flexShrink: 0 }} />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => plan.cta === "Talk to Sales" ? window.location.href = DEMO_CALENDLY_URL : navigate("/login")}
                data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                style={{
                  width: "100%", background: plan.highlight ? plan.accent : "transparent",
                  border: `1px solid ${plan.accent}`, borderRadius: 8,
                  padding: "11px 16px", color: plan.highlight ? "#080c12" : plan.accent,
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Data Sources */}
      <section id="data-sources" style={{ padding: "60px 40px", background: "#0d1117" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: "28px", fontWeight: 700, marginBottom: "12px" }}>
            <span style={{ color: "#FFD700" }}>15+</span> India-Specific Data Sources
          </h2>
          <p style={{ fontSize: "14px", color: "#475569", marginBottom: "32px" }}>
            No other AML tool covers this depth of Indian regulatory databases
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            {SOURCES.map(src => (
              <span key={src} style={{
                padding: "8px 16px", borderRadius: "8px", fontSize: "12px", fontWeight: 500,
                background: "#080c12", border: "1px solid #1e2530", color: "#94a3b8",
              }}>
                {src}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "80px 40px", textAlign: "center" }}>
        <div style={{
          maxWidth: "700px", margin: "0 auto", padding: "48px",
          background: "rgba(37,99,235,0.04)", border: "1px solid rgba(37,99,235,0.15)",
          borderRadius: "16px",
        }}>
          <h2 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "12px" }}>
            Ready to make your compliance RBI-proof?
          </h2>
          <p style={{ fontSize: "14px", color: "#94a3b8", marginBottom: "24px" }}>
            Screen your first entity in under 60 seconds. No setup required.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button onClick={() => navigate("/login")} data-testid="cta-get-started" style={{
              background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "10px",
              padding: "14px 32px", cursor: "pointer", color: "#fff", fontSize: "15px", fontWeight: 600,
              boxShadow: "0 8px 32px rgba(37,99,235,0.3)",
            }}>
              Get Started Free
            </button>
            <a href={DEMO_CALENDLY_URL} data-testid="cta-book-demo" style={{
              background: "transparent", border: "1px solid #1e2530", borderRadius: "10px",
              padding: "14px 32px", color: "#94a3b8", fontSize: "15px", fontWeight: 600, textDecoration: "none",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              Book 30-min Demo
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: "32px 40px", borderTop: "1px solid #1e2530",
      }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <img src="/assets/rudrik-logo-full.png" alt="Rudrik" style={{ height: "28px", width: "auto", objectFit: "contain" }} />
            <div className="flex items-center gap-5 flex-wrap" data-testid="footer-legal-links">
              <a href="/privacy" data-testid="footer-privacy" style={footerLinkStyle}>Privacy Policy</a>
              <a href="/terms" data-testid="footer-terms" style={footerLinkStyle}>Terms of Service</a>
              <a href="/data-policy" data-testid="footer-data-policy" style={footerLinkStyle}>Data Policy</a>
              <a href="mailto:contact@rudrik.io" style={footerLinkStyle}>contact@rudrik.io</a>
            </div>
          </div>
          <div style={{ fontSize: "11px", color: "#334155", borderTop: "1px solid #1e2530", paddingTop: 16 }}>
            &copy; 2026 Rudrik Technologies Private Limited &middot; <a href="https://rudrik.io" style={{ color: "#2563eb", textDecoration: "none" }}>rudrik.io</a>
          </div>
        </div>
      </footer>

      {/* WhatsApp floating button */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Rudrik team — I'd like to know more about your AML platform.")}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="landing-whatsapp-btn"
        title="Chat on WhatsApp"
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 100,
          width: 54, height: 54, borderRadius: "50%",
          background: "#25D366", boxShadow: "0 6px 24px rgba(37,211,102,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          textDecoration: "none", cursor: "pointer",
          transition: "transform 0.2s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.06)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        <MessageCircle style={{ width: 26, height: 26, color: "#fff" }} />
      </a>
    </div>
  );
}

const navLinkStyle = {
  background: "transparent", border: "none", cursor: "pointer",
  color: "#94a3b8", fontSize: 13, fontWeight: 500, textDecoration: "none",
  padding: "6px 4px",
};

const footerLinkStyle = {
  fontSize: 12, color: "#94a3b8", textDecoration: "none",
};


// -----------------------------------------------------------------------------
// StatCell — icon + animated count-up + label, with left divider
// -----------------------------------------------------------------------------
function useCountUp(target, duration = 1200, start = 0) {
  const [n, setN] = useState(start);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    if (target == null) return;
    if (started.current) return;
    const el = ref.current;
    const run = () => {
      const t0 = performance.now();
      const from = 0;
      const step = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setN(from + (target - from) * eased);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
      started.current = true;
    };
    if (!("IntersectionObserver" in window) || !el) return run();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { run(); io.disconnect(); } });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);
  return [n, ref];
}

function StatCell({ stat, showDivider }) {
  const I = stat.icon;
  const isNumeric = typeof stat.value === "number";
  const [count, ref] = useCountUp(isNumeric ? stat.value : null, 1200);
  const display = isNumeric
    ? `${Math.round(count)}${stat.suffix || ""}`
    : stat.raw;
  return (
    <div ref={ref} className="rd-stat-item"
         data-testid={`landing-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
         style={{
           padding: "22px 24px",
           borderLeft: showDivider ? "1px solid #14182a" : "none",
           display: "flex", alignItems: "center", gap: 14,
           position: "relative", minWidth: 0,
         }}>
      <div className="rd-stat-icon" style={{
        width: 40, height: 40, flexShrink: 0, borderRadius: 10,
        background: `${stat.accent}18`,
        border: `1px solid ${stat.accent}44`,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "transform .3s ease",
      }}>
        <I size={17} color={stat.accent} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          color: "#f1f5f9", fontSize: 22, fontWeight: 800, lineHeight: 1.1,
          letterSpacing: "-0.5px",
          fontFamily: "'Geist Mono', ui-monospace, 'DM Sans', sans-serif",
        }}>
          {display}
        </div>
        <div style={{
          color: "#64748b", fontSize: 10, fontWeight: 700,
          letterSpacing: 1, textTransform: "uppercase", marginTop: 3,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {stat.label}
        </div>
      </div>
    </div>
  );
}
